import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { InternalLayout } from "@/components/InternalLayout";
import { fmtDate, ROLE_LABELS } from "@/lib/constants";
import { Plus, Trash2, Settings as SettingsIcon, KeyRound, FileCode2, Lock } from "lucide-react";
import { toast } from "sonner";
import { MultiProviderTest } from "./MultiProviderTest";
import { ArchetypesTab } from "./ArchetypesTab";

const PROVIDER_PRESETS = {
  openai: { models: ["gpt-5.5", "gpt-5.2", "gpt-4o"] },
  anthropic: { models: ["claude-opus-4-8", "claude-sonnet-4-6", "claude-haiku-4-5"] },
  google: { models: ["gemini-3.1-pro-preview", "gemini-3-pro", "gemini-3-flash"] },
  deepseek: { models: ["deepseek-reasoner", "deepseek-chat"] },
  openrouter: { models: ["openrouter/auto"] },
  kimi: { models: ["kimi-k2.6"] },
  custom: { models: [] },
};

const TPL_STATUS_LABEL = {
  draft: "Entwurf",
  in_review: "Im Review",
  active: "Aktiv",
  archived: "Archiviert",
};
const TPL_STATUS_STYLE = {
  draft: "bg-neutral-100 text-neutral-700 border-neutral-300",
  in_review: "bg-amber-50 text-amber-800 border-amber-300",
  active: "bg-emerald-50 text-emerald-800 border-emerald-300",
  archived: "bg-neutral-50 text-neutral-500 border-neutral-200",
};
// Übergangs-Whitelist (spiegelt TEMPLATE_STATUS_TRANSITIONS in server.py)
const TPL_TRANSITIONS = {
  draft: ["in_review", "archived"],
  in_review: ["active", "draft", "archived"],
  active: ["archived"],
  archived: [],
};

export default function Settings() {
  const { hasRole } = useAuth();
  const canManageTemplates = hasRole("intern_architekt", "admin");
  const isAdmin = hasRole("admin");

  const [tab, setTab] = useState("llm");
  const [configs, setConfigs] = useState([]);
  const [users, setUsers] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({ name: "", provider: "openai", model: "gpt-5.2", api_key: "", base_url: "", scope: "workspace" });

  // Prompt-Templates State
  const [templates, setTemplates] = useState([]);
  const [tplFilter, setTplFilter] = useState("");
  const [tplShowNew, setTplShowNew] = useState(false);
  const [tplForm, setTplForm] = useState({ purpose: "", name: "", prompt_text: "", description: "", variables: "" });
  const [tplDetail, setTplDetail] = useState(null); // ausgewähltes Template für Detail-Modal

  const loadConfigs = async () => {
    const r = await api.get("/llm-configs");
    setConfigs(r.data);
  };
  const loadUsers = async () => {
    const r = await api.get("/users");
    setUsers(r.data);
  };
  const loadTemplates = async () => {
    const r = await api.get("/discovery/prompt-templates");
    setTemplates(r.data);
  };
  useEffect(() => { loadConfigs(); loadUsers(); loadTemplates(); }, []);

  const onCreate = async (e) => {
    e.preventDefault();
    try {
      await api.post("/llm-configs", form);
      toast.success("LLM-Config gespeichert");
      setShowNew(false);
      setForm({ name: "", provider: "openai", model: "gpt-5.2", api_key: "", base_url: "", scope: "workspace" });
      loadConfigs();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Anlegen fehlgeschlagen");
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Wirklich löschen?")) return;
    await api.delete(`/llm-configs/${id}`);
    toast.success("Gelöscht");
    loadConfigs();
  };

  const setRoles = async (uid, roles) => {
    try {
      await api.patch(`/users/${uid}/roles`, { roles });
      toast.success("Rollen aktualisiert");
      loadUsers();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Aktualisierung fehlgeschlagen");
    }
  };

  const ALL_ROLES = ["admin", "intern_delivery", "intern_architekt", "intern_audit", "kunde_entscheider", "kunde_mitwirkend", "externer_reviewer"];

  // ── Prompt-Template Handler ──
  const onTplCreate = async (e) => {
    e.preventDefault();
    try {
      const variables = tplForm.variables.split(",").map((s) => s.trim()).filter(Boolean);
      await api.post("/discovery/prompt-templates", {
        purpose: tplForm.purpose,
        name: tplForm.name,
        prompt_text: tplForm.prompt_text,
        description: tplForm.description,
        variables,
      });
      toast.success("Template angelegt (Status: Entwurf)");
      setTplShowNew(false);
      setTplForm({ purpose: "", name: "", prompt_text: "", description: "", variables: "" });
      loadTemplates();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Anlegen fehlgeschlagen");
    }
  };

  const onTplUpdate = async (id, fields) => {
    try {
      await api.patch(`/discovery/prompt-templates/${id}`, fields);
      toast.success("Template aktualisiert");
      const fresh = await api.get("/discovery/prompt-templates");
      setTemplates(fresh.data);
      const updated = fresh.data.find((t) => t.template_id === id);
      if (updated && tplDetail?.template_id === id) setTplDetail(updated);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Update fehlgeschlagen");
    }
  };

  const onTplStatus = async (id, newStatus, change_note) => {
    try {
      await api.post(`/discovery/prompt-templates/${id}/status`, { status: newStatus, change_note });
      toast.success(`Status → ${TPL_STATUS_LABEL[newStatus]}`);
      const fresh = await api.get("/discovery/prompt-templates");
      setTemplates(fresh.data);
      const updated = fresh.data.find((t) => t.template_id === id);
      if (updated && tplDetail?.template_id === id) setTplDetail(updated);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Statuswechsel fehlgeschlagen");
    }
  };

  const filteredTemplates = templates.filter((t) =>
    !tplFilter || t.purpose.toLowerCase().includes(tplFilter.toLowerCase()) || t.name.toLowerCase().includes(tplFilter.toLowerCase())
  );

  return (
    <InternalLayout>
      <div className="mb-6">
        <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-neutral-500 mb-2">Konfiguration</div>
        <h1 className="font-display text-3xl md:text-4xl text-neutral-900 flex items-center gap-3">Einstellungen <SettingsIcon className="w-6 h-6 text-[#183153]" /></h1>
      </div>

      <div className="mb-6 border-b border-neutral-200">
        <div className="flex gap-6">
          <button onClick={() => setTab("llm")} data-testid="tab-llm" className={`pb-3 text-sm border-b-2 ${tab === "llm" ? "border-[#183153] text-neutral-900 font-medium" : "border-transparent text-neutral-500"}`}>LLM-Konfiguration</button>
          <button onClick={() => setTab("templates")} data-testid="tab-templates" className={`pb-3 text-sm border-b-2 ${tab === "templates" ? "border-[#183153] text-neutral-900 font-medium" : "border-transparent text-neutral-500"}`}>Prompt-Templates</button>
          <button onClick={() => setTab("multi")} data-testid="tab-multi" className={`pb-3 text-sm border-b-2 ${tab === "multi" ? "border-[#183153] text-neutral-900 font-medium" : "border-transparent text-neutral-500"}`}>Multi-Provider-Test</button>
          <button onClick={() => setTab("archetypes")} data-testid="tab-archetypes" className={`pb-3 text-sm border-b-2 ${tab === "archetypes" ? "border-[#183153] text-neutral-900 font-medium" : "border-transparent text-neutral-500"}`}>Archetypen</button>
          <button onClick={() => setTab("team")} data-testid="tab-team" className={`pb-3 text-sm border-b-2 ${tab === "team" ? "border-[#183153] text-neutral-900 font-medium" : "border-transparent text-neutral-500"}`}>Team & Rollen</button>
        </div>
      </div>

      {tab === "llm" && (
        <>
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm text-neutral-600">API-Tokens für verschiedene LLM-Provider verwalten. Tokens werden verschlüsselt gespeichert.</p>
            <button onClick={() => setShowNew(true)} data-testid="llm-new-button" className="inline-flex items-center gap-2 px-3 py-2 text-sm text-white rounded-sm" style={{ background: "#183153" }}>
              <Plus className="w-4 h-4" /> Neue Config
            </button>
          </div>
          <div className="bg-white border border-neutral-200">
            <table className="w-full text-sm">
              <thead className="border-b border-neutral-200 bg-neutral-50">
                <tr className="text-left">
                  <th className="px-5 py-2 font-mono text-[10px] uppercase tracking-widest text-neutral-500">Name</th>
                  <th className="px-5 py-2 font-mono text-[10px] uppercase tracking-widest text-neutral-500">Provider</th>
                  <th className="px-5 py-2 font-mono text-[10px] uppercase tracking-widest text-neutral-500">Modell</th>
                  <th className="px-5 py-2 font-mono text-[10px] uppercase tracking-widest text-neutral-500">API-Key</th>
                  <th className="px-5 py-2 font-mono text-[10px] uppercase tracking-widest text-neutral-500">Scope</th>
                  <th className="px-5 py-2 font-mono text-[10px] uppercase tracking-widest text-neutral-500"></th>
                </tr>
              </thead>
              <tbody>
                {configs.length === 0 && <tr><td colSpan={6} className="px-5 py-10 text-center text-neutral-500">Keine LLM-Configs angelegt.</td></tr>}
                {configs.map((c) => (
                  <tr key={c.config_id} className="border-b border-neutral-100">
                    <td className="px-5 py-3 text-sm font-medium">{c.name}</td>
                    <td className="px-5 py-3 font-mono text-xs">{c.provider}</td>
                    <td className="px-5 py-3 font-mono text-xs">{c.model}</td>
                    <td className="px-5 py-3 font-mono text-xs flex items-center gap-2"><KeyRound className="w-3 h-3" />{c.api_key_masked}</td>
                    <td className="px-5 py-3 text-xs">{c.scope}</td>
                    <td className="px-5 py-3 text-right">
                      <button onClick={() => onDelete(c.config_id)} data-testid={`llm-delete-${c.config_id}`} className="text-red-600 hover:bg-red-50 p-1.5"><Trash2 className="w-3.5 h-3.5" /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {showNew && (
            <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setShowNew(false)}>
              <div className="bg-white border border-neutral-200 max-w-lg w-full" onClick={(e) => e.stopPropagation()} data-testid="llm-modal">
                <div className="px-5 py-3 border-b border-neutral-200 font-display-medium">Neue LLM-Konfiguration</div>
                <form onSubmit={onCreate} className="p-5 space-y-3">
                  <input required placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="llm-name-input" className="w-full px-3 py-2 border border-neutral-300 rounded-sm text-sm" />
                  <div className="grid grid-cols-2 gap-3">
                    <select value={form.provider} onChange={(e) => setForm({ ...form, provider: e.target.value, model: PROVIDER_PRESETS[e.target.value]?.models[0] || "" })} data-testid="llm-provider-select" className="px-3 py-2 border border-neutral-300 rounded-sm text-sm">
                      <option value="openai">OpenAI</option>
                      <option value="anthropic">Anthropic</option>
                      <option value="google">Google</option>
                      <option value="deepseek">DeepSeek</option>
                      <option value="openrouter">OpenRouter</option>
                      <option value="kimi">Kimi</option>
                      <option value="custom">Custom</option>
                    </select>
                    {PROVIDER_PRESETS[form.provider]?.models.length > 0 ? (
                      <select value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })} className="px-3 py-2 border border-neutral-300 rounded-sm text-sm">
                        {PROVIDER_PRESETS[form.provider].models.map((m) => <option key={m} value={m}>{m}</option>)}
                      </select>
                    ) : (
                      <input placeholder="Modell" value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })} className="px-3 py-2 border border-neutral-300 rounded-sm text-sm font-mono" />
                    )}
                  </div>
                  <input required placeholder="API Key" value={form.api_key} onChange={(e) => setForm({ ...form, api_key: e.target.value })} data-testid="llm-key-input" className="w-full px-3 py-2 border border-neutral-300 rounded-sm text-sm font-mono" />
                  <input placeholder="Base URL (optional)" value={form.base_url} onChange={(e) => setForm({ ...form, base_url: e.target.value })} className="w-full px-3 py-2 border border-neutral-300 rounded-sm text-sm font-mono" />
                  <div className="flex justify-end gap-2 pt-2">
                    <button type="button" onClick={() => setShowNew(false)} className="px-4 py-2 text-sm border border-neutral-300 rounded-sm hover:bg-neutral-50">Abbrechen</button>
                    <button type="submit" data-testid="llm-submit-button" className="px-4 py-2 text-sm text-white rounded-sm" style={{ background: "#183153" }}>Speichern</button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </>
      )}

      {tab === "templates" && (
        <>
          <div className="flex items-center justify-between mb-4 gap-3">
            <div className="flex-1">
              <p className="text-sm text-neutral-600">Prompt-Templates für Discovery 2.0. Status-Workflow: Entwurf → Im Review → Aktiv → Archiviert. Aktive Templates sind eingefroren.</p>
            </div>
            <input
              placeholder="Filter (purpose, name)…"
              value={tplFilter}
              onChange={(e) => setTplFilter(e.target.value)}
              data-testid="tpl-filter-input"
              className="px-3 py-2 border border-neutral-300 rounded-sm text-sm w-64"
            />
            {canManageTemplates && (
              <button onClick={() => setTplShowNew(true)} data-testid="tpl-new-button" className="inline-flex items-center gap-2 px-3 py-2 text-sm text-white rounded-sm whitespace-nowrap" style={{ background: "#183153" }}>
                <Plus className="w-4 h-4" /> Neues Template
              </button>
            )}
          </div>

          {!canManageTemplates && (
            <div className="mb-4 p-3 bg-amber-50 border border-amber-200 text-xs text-amber-900 flex items-center gap-2">
              <Lock className="w-3.5 h-3.5" /> Nur Lesezugriff. Anlage und Bearbeitung erfordern Rolle <code>intern_architekt</code> oder <code>admin</code>.
            </div>
          )}

          <div className="bg-white border border-neutral-200">
            <table className="w-full text-sm">
              <thead className="border-b border-neutral-200 bg-neutral-50">
                <tr className="text-left">
                  <th className="px-5 py-2 font-mono text-[10px] uppercase tracking-widest text-neutral-500">Purpose</th>
                  <th className="px-5 py-2 font-mono text-[10px] uppercase tracking-widest text-neutral-500">Name</th>
                  <th className="px-5 py-2 font-mono text-[10px] uppercase tracking-widest text-neutral-500">Version</th>
                  <th className="px-5 py-2 font-mono text-[10px] uppercase tracking-widest text-neutral-500">Status</th>
                  <th className="px-5 py-2 font-mono text-[10px] uppercase tracking-widest text-neutral-500">Geändert</th>
                  <th className="px-5 py-2 font-mono text-[10px] uppercase tracking-widest text-neutral-500"></th>
                </tr>
              </thead>
              <tbody>
                {filteredTemplates.length === 0 && <tr><td colSpan={6} className="px-5 py-10 text-center text-neutral-500" data-testid="tpl-empty-state">Keine Prompt-Templates angelegt.</td></tr>}
                {filteredTemplates.map((t) => (
                  <tr key={t.template_id} className="border-b border-neutral-100 hover:bg-neutral-50 cursor-pointer" onClick={() => setTplDetail(t)} data-testid={`tpl-row-${t.template_id}`}>
                    <td className="px-5 py-3 font-mono text-xs">{t.purpose}</td>
                    <td className="px-5 py-3 text-sm font-medium">{t.name}</td>
                    <td className="px-5 py-3 font-mono text-xs">v{t.version}</td>
                    <td className="px-5 py-3">
                      <span className={`text-[10px] px-2 py-0.5 border rounded-sm ${TPL_STATUS_STYLE[t.status]}`} data-testid={`tpl-status-${t.template_id}`}>
                        {TPL_STATUS_LABEL[t.status]}
                      </span>
                    </td>
                    <td className="px-5 py-3 font-mono text-xs text-neutral-500">{fmtDate(t.changed_at)}</td>
                    <td className="px-5 py-3 text-right">
                      <FileCode2 className="w-3.5 h-3.5 text-neutral-400 inline" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {tplShowNew && canManageTemplates && (
            <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setTplShowNew(false)}>
              <div className="bg-white border border-neutral-200 max-w-xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="tpl-new-modal">
                <div className="px-5 py-3 border-b border-neutral-200 font-display-medium">Neues Prompt-Template</div>
                <form onSubmit={onTplCreate} className="p-5 space-y-3">
                  <div>
                    <label className="block text-[11px] font-mono uppercase tracking-widest text-neutral-500 mb-1">Purpose <span className="text-red-600">*</span></label>
                    <input required placeholder="z.B. interview_moderator, hypothesis_generator, summary_writer" value={tplForm.purpose} onChange={(e) => setTplForm({ ...tplForm, purpose: e.target.value })} data-testid="tpl-purpose-input" className="w-full px-3 py-2 border border-neutral-300 rounded-sm text-sm font-mono" />
                  </div>
                  <div>
                    <label className="block text-[11px] font-mono uppercase tracking-widest text-neutral-500 mb-1">Name <span className="text-red-600">*</span></label>
                    <input required placeholder="Lesbarer Name" value={tplForm.name} onChange={(e) => setTplForm({ ...tplForm, name: e.target.value })} data-testid="tpl-name-input" className="w-full px-3 py-2 border border-neutral-300 rounded-sm text-sm" />
                  </div>
                  <div>
                    <label className="block text-[11px] font-mono uppercase tracking-widest text-neutral-500 mb-1">Beschreibung</label>
                    <input placeholder="Wann/wofür wird das Template eingesetzt?" value={tplForm.description} onChange={(e) => setTplForm({ ...tplForm, description: e.target.value })} className="w-full px-3 py-2 border border-neutral-300 rounded-sm text-sm" />
                  </div>
                  <div>
                    <label className="block text-[11px] font-mono uppercase tracking-widest text-neutral-500 mb-1">Variablen (komma-getrennt)</label>
                    <input placeholder="topic, project_context, language" value={tplForm.variables} onChange={(e) => setTplForm({ ...tplForm, variables: e.target.value })} className="w-full px-3 py-2 border border-neutral-300 rounded-sm text-sm font-mono" />
                  </div>
                  <div>
                    <label className="block text-[11px] font-mono uppercase tracking-widest text-neutral-500 mb-1">Prompt-Text <span className="text-red-600">*</span></label>
                    <textarea required rows={10} placeholder="Du bist ein Discovery-Moderator. {{topic}} ..." value={tplForm.prompt_text} onChange={(e) => setTplForm({ ...tplForm, prompt_text: e.target.value })} data-testid="tpl-text-input" className="w-full px-3 py-2 border border-neutral-300 rounded-sm text-sm font-mono" />
                  </div>
                  <div className="flex justify-end gap-2 pt-2">
                    <button type="button" onClick={() => setTplShowNew(false)} className="px-4 py-2 text-sm border border-neutral-300 rounded-sm hover:bg-neutral-50">Abbrechen</button>
                    <button type="submit" data-testid="tpl-submit-button" className="px-4 py-2 text-sm text-white rounded-sm" style={{ background: "#183153" }}>Als Entwurf speichern</button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {tplDetail && (
            <TemplateDetailModal
              tpl={tplDetail}
              canEdit={canManageTemplates && (tplDetail.status === "draft" || tplDetail.status === "in_review")}
              isAdmin={isAdmin}
              canManage={canManageTemplates}
              onClose={() => setTplDetail(null)}
              onUpdate={(fields) => onTplUpdate(tplDetail.template_id, fields)}
              onStatus={(s, note) => onTplStatus(tplDetail.template_id, s, note)}
            />
          )}
        </>
      )}

      {tab === "multi" && <MultiProviderTest />}

      {tab === "archetypes" && <ArchetypesTab />}

      {tab === "team" && (
        <div className="bg-white border border-neutral-200">
          <table className="w-full text-sm">
            <thead className="border-b border-neutral-200 bg-neutral-50">
              <tr className="text-left">
                <th className="px-5 py-2 font-mono text-[10px] uppercase tracking-widest text-neutral-500">Name</th>
                <th className="px-5 py-2 font-mono text-[10px] uppercase tracking-widest text-neutral-500">E-Mail</th>
                <th className="px-5 py-2 font-mono text-[10px] uppercase tracking-widest text-neutral-500">Auth</th>
                <th className="px-5 py-2 font-mono text-[10px] uppercase tracking-widest text-neutral-500">Rollen</th>
                <th className="px-5 py-2 font-mono text-[10px] uppercase tracking-widest text-neutral-500">Erstellt</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.user_id} className="border-b border-neutral-100">
                  <td className="px-5 py-3">{u.name}</td>
                  <td className="px-5 py-3 font-mono text-xs">{u.email}</td>
                  <td className="px-5 py-3 text-xs">{u.auth_provider}</td>
                  <td className="px-5 py-3">
                    <div className="flex flex-wrap gap-1">
                      {ALL_ROLES.map((r) => {
                        const has = (u.roles || []).includes(r);
                        return (
                          <button
                            key={r}
                            onClick={() => setRoles(u.user_id, has ? u.roles.filter((x) => x !== r) : [...(u.roles || []), r])}
                            data-testid={`user-${u.user_id}-role-${r}`}
                            className={`text-[10px] px-2 py-0.5 border rounded-sm ${has ? "bg-[#183153] text-white border-[#183153]" : "bg-white text-neutral-700 border-neutral-300 hover:bg-neutral-50"}`}
                          >
                            {ROLE_LABELS[r]}
                          </button>
                        );
                      })}
                    </div>
                  </td>
                  <td className="px-5 py-3 font-mono text-xs text-neutral-500">{fmtDate(u.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </InternalLayout>
  );
}

// ── Detail-Modal: Inhalts-Editor + Status-Buttons + Trockendurchlauf ──
function TemplateDetailModal({ tpl, canEdit, isAdmin, canManage, onClose, onUpdate, onStatus }) {
  const [editing, setEditing] = React.useState(false);
  const [draft, setDraft] = React.useState({
    name: tpl.name,
    description: tpl.description || "",
    variables: (tpl.variables || []).join(", "),
    prompt_text: tpl.prompt_text,
    change_note: "",
  });
  const [pendingStatus, setPendingStatus] = React.useState(null);

  // ── Trockendurchlauf-State ──
  const dryAllowed = canManage && (tpl.status === "draft" || tpl.status === "in_review");
  const [dryOpen, setDryOpen] = React.useState(false);
  const [dryConfigs, setDryConfigs] = React.useState([]);
  const [dryConfigId, setDryConfigId] = React.useState("");
  const [dryVars, setDryVars] = React.useState(
    (tpl.variables || []).map((k) => ({ key: k, value: "" }))
  );
  const [dryRunning, setDryRunning] = React.useState(false);
  const [dryResult, setDryResult] = React.useState(null);  // {ok:true, body} | {ok:false, msg}

  React.useEffect(() => {
    setDraft({
      name: tpl.name,
      description: tpl.description || "",
      variables: (tpl.variables || []).join(", "),
      prompt_text: tpl.prompt_text,
      change_note: "",
    });
    setEditing(false);
    setPendingStatus(null);
    setDryOpen(false);
    setDryResult(null);
    setDryVars((tpl.variables || []).map((k) => ({ key: k, value: "" })));
  }, [tpl.template_id, tpl.changed_at, tpl.variables, tpl.name, tpl.description, tpl.prompt_text]);

  // Configs für Dropdown laden, wenn Trockendurchlauf-Panel geöffnet wird
  React.useEffect(() => {
    if (!dryOpen || dryConfigs.length > 0) return;
    api.get("/llm-configs").then((r) => {
      const supported = r.data.filter((c) => ["openai", "anthropic", "google"].includes(c.provider));
      setDryConfigs(supported);
      if (supported.length > 0 && !dryConfigId) setDryConfigId(supported[0].config_id);
    });
  }, [dryOpen, dryConfigs.length, dryConfigId]);

  const allowedTargets = TPL_TRANSITIONS[tpl.status] || [];
  const canDoStatus = (s) => {
    if (!canManage) return false;
    if (s === "active") return isAdmin;
    return true;
  };

  const submitEdit = (e) => {
    e.preventDefault();
    const variables = draft.variables.split(",").map((v) => v.trim()).filter(Boolean);
    onUpdate({
      name: draft.name,
      description: draft.description,
      variables,
      prompt_text: draft.prompt_text,
      change_note: draft.change_note || "Inhaltsänderung",
    });
    setEditing(false);
  };

  const submitStatus = () => {
    if (!pendingStatus) return;
    onStatus(pendingStatus.target, pendingStatus.note);
    setPendingStatus(null);
  };

  // ── Variablen-Substitution für Trockendurchlauf ──
  const substituteVariables = (text, varList) => {
    let result = text;
    for (const { key, value } of varList) {
      if (!key.trim()) continue;
      const re = new RegExp(`\\{\\{\\s*${key.trim()}\\s*\\}\\}`, "g");
      result = result.replace(re, value);
    }
    return result;
  };

  const startDryRun = async () => {
    if (!dryConfigId) {
      toast.error("Bitte LLM-Konfiguration auswählen");
      return;
    }
    if (dryVars.some((v) => v.key.trim() && !v.value.trim())) {
      toast.error("Variablen ohne Wert vorhanden");
      return;
    }
    setDryRunning(true);
    setDryResult(null);
    try {
      const substituted = substituteVariables(tpl.prompt_text, dryVars);
      const r = await api.post("/llm/generate", {
        config_id: dryConfigId,
        template_id: tpl.template_id,
        dry_run: true,
        system_prompt: "Du bist ein hilfsbereiter Assistent für Discovery-Aufgaben.",
        user_prompt: substituted,
      });
      setDryResult({ ok: true, body: r.data });
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail || "Unerwarteter Fehler";
      setDryResult({ ok: false, msg: `[${status || "?"}] ${detail}` });
    } finally {
      setDryRunning(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white border border-neutral-200 max-w-3xl w-full max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="tpl-detail-modal">
        <div className="px-5 py-3 border-b border-neutral-200 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">{tpl.purpose} · v{tpl.version}</div>
            <div className="font-display text-lg text-neutral-900 truncate">{tpl.name}</div>
          </div>
          <span className={`text-[10px] px-2 py-0.5 border rounded-sm whitespace-nowrap ${TPL_STATUS_STYLE[tpl.status]}`} data-testid="tpl-detail-status">
            {TPL_STATUS_LABEL[tpl.status]}
          </span>
        </div>

        <div className="p-5 space-y-4">
          {tpl.description && !editing && (
            <p className="text-sm text-neutral-600">{tpl.description}</p>
          )}

          {!editing && (
            <>
              {tpl.variables?.length > 0 && (
                <div>
                  <div className="font-mono text-[10px] uppercase tracking-widest text-neutral-500 mb-1">Variablen</div>
                  <div className="flex flex-wrap gap-1">
                    {tpl.variables.map((v) => <code key={v} className="text-[11px] px-2 py-0.5 bg-neutral-100 border border-neutral-200">{v}</code>)}
                  </div>
                </div>
              )}
              <div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-neutral-500 mb-1">Prompt-Text</div>
                <pre className="text-xs font-mono whitespace-pre-wrap bg-neutral-50 border border-neutral-200 p-3 max-h-80 overflow-y-auto" data-testid="tpl-detail-text">{tpl.prompt_text}</pre>
              </div>
              <div className="grid grid-cols-2 gap-4 text-xs text-neutral-500 pt-2 border-t border-neutral-100">
                <div><span className="font-mono uppercase tracking-widest text-[10px]">Erstellt</span><br/>{tpl.created_at?.slice(0, 19).replace("T", " ")}</div>
                <div><span className="font-mono uppercase tracking-widest text-[10px]">Geändert</span><br/>{tpl.changed_at?.slice(0, 19).replace("T", " ")}<br/><span className="italic">{tpl.change_note}</span></div>
              </div>
            </>
          )}

          {editing && (
            <form onSubmit={submitEdit} className="space-y-3" data-testid="tpl-edit-form">
              <input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} className="w-full px-3 py-2 border border-neutral-300 rounded-sm text-sm" placeholder="Name" />
              <input value={draft.description} onChange={(e) => setDraft({ ...draft, description: e.target.value })} className="w-full px-3 py-2 border border-neutral-300 rounded-sm text-sm" placeholder="Beschreibung" />
              <input value={draft.variables} onChange={(e) => setDraft({ ...draft, variables: e.target.value })} className="w-full px-3 py-2 border border-neutral-300 rounded-sm text-sm font-mono" placeholder="Variablen (komma-getrennt)" />
              <textarea rows={12} value={draft.prompt_text} onChange={(e) => setDraft({ ...draft, prompt_text: e.target.value })} className="w-full px-3 py-2 border border-neutral-300 rounded-sm text-sm font-mono" />
              <input value={draft.change_note} onChange={(e) => setDraft({ ...draft, change_note: e.target.value })} className="w-full px-3 py-2 border border-neutral-300 rounded-sm text-sm" placeholder="Change-Note (Pflicht für Audit-Trail)" />
              <div className="flex justify-end gap-2">
                <button type="button" onClick={() => setEditing(false)} className="px-4 py-2 text-sm border border-neutral-300 rounded-sm hover:bg-neutral-50">Abbrechen</button>
                <button type="submit" data-testid="tpl-edit-save" className="px-4 py-2 text-sm text-white rounded-sm" style={{ background: "#183153" }}>Speichern</button>
              </div>
            </form>
          )}

          {pendingStatus && (
            <div className="border border-amber-300 bg-amber-50 p-3 rounded-sm space-y-2" data-testid="tpl-status-confirm">
              <div className="text-sm font-medium text-amber-900">
                Status wechseln: {TPL_STATUS_LABEL[tpl.status]} → {TPL_STATUS_LABEL[pendingStatus.target]}
              </div>
              <input
                autoFocus
                placeholder="Begründung / Change-Note (Pflicht)"
                value={pendingStatus.note}
                onChange={(e) => setPendingStatus({ ...pendingStatus, note: e.target.value })}
                data-testid="tpl-status-note-input"
                className="w-full px-3 py-2 border border-amber-300 rounded-sm text-sm bg-white"
              />
              <div className="flex justify-end gap-2">
                <button onClick={() => setPendingStatus(null)} className="px-3 py-1.5 text-xs border border-neutral-300 rounded-sm hover:bg-neutral-50">Abbrechen</button>
                <button
                  onClick={submitStatus}
                  disabled={!pendingStatus.note.trim()}
                  data-testid="tpl-status-confirm-button"
                  className="px-3 py-1.5 text-xs text-white rounded-sm disabled:opacity-50"
                  style={{ background: "#183153" }}
                >
                  Bestätigen
                </button>
              </div>
            </div>
          )}

          {/* ── Trockendurchlauf-Panel ── */}
          {dryOpen && (
            <div className="border border-[#183153]/30 bg-[#183153]/5 p-4 rounded-sm space-y-3" data-testid="tpl-dryrun-panel">
              <div className="flex items-center justify-between gap-2">
                <div>
                  <div className="font-mono text-[10px] uppercase tracking-widest text-[#183153]">Trockendurchlauf</div>
                  <div className="text-[11px] text-neutral-600 mt-0.5">QA-Hilfe — Ergebnis wird nicht persistiert. Nur Metadaten landen in der Provenienz-Spur.</div>
                </div>
                <button onClick={() => setDryOpen(false)} className="text-xs text-neutral-500 hover:text-neutral-900" data-testid="tpl-dryrun-close">Schließen</button>
              </div>

              <div>
                <label className="block text-[11px] font-mono uppercase tracking-widest text-neutral-500 mb-1">LLM-Konfiguration</label>
                {dryConfigs.length === 0 ? (
                  <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 px-3 py-2">Keine kompatible Konfiguration vorhanden. Lege im Tab „LLM-Konfiguration" eine Config mit Provider openai / anthropic / google an.</div>
                ) : (
                  <select
                    value={dryConfigId}
                    onChange={(e) => setDryConfigId(e.target.value)}
                    data-testid="tpl-dryrun-config-select"
                    className="w-full px-3 py-2 border border-neutral-300 rounded-sm text-sm font-mono bg-white"
                  >
                    {dryConfigs.map((c) => (
                      <option key={c.config_id} value={c.config_id}>{c.name} · {c.provider} · {c.model}</option>
                    ))}
                  </select>
                )}
              </div>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-[11px] font-mono uppercase tracking-widest text-neutral-500">Variablen</label>
                  <button
                    type="button"
                    onClick={() => setDryVars([...dryVars, { key: "", value: "" }])}
                    data-testid="tpl-dryrun-add-var"
                    className="text-[11px] text-[#183153] hover:underline"
                  >+ Variable hinzufügen</button>
                </div>
                {dryVars.length === 0 ? (
                  <div className="text-xs text-neutral-500 italic">Keine Variablen — Prompt wird unverändert verwendet.</div>
                ) : (
                  <div className="space-y-2">
                    {dryVars.map((v, idx) => (
                      <div key={idx} className="flex gap-2">
                        <input
                          placeholder="key"
                          value={v.key}
                          onChange={(e) => {
                            const copy = [...dryVars];
                            copy[idx] = { ...copy[idx], key: e.target.value };
                            setDryVars(copy);
                          }}
                          data-testid={`tpl-dryrun-key-${idx}`}
                          className="w-1/3 px-3 py-1.5 border border-neutral-300 rounded-sm text-xs font-mono bg-white"
                        />
                        <input
                          placeholder="value"
                          value={v.value}
                          onChange={(e) => {
                            const copy = [...dryVars];
                            copy[idx] = { ...copy[idx], value: e.target.value };
                            setDryVars(copy);
                          }}
                          data-testid={`tpl-dryrun-value-${idx}`}
                          className="flex-1 px-3 py-1.5 border border-neutral-300 rounded-sm text-xs bg-white"
                        />
                        <button
                          type="button"
                          onClick={() => setDryVars(dryVars.filter((_, i) => i !== idx))}
                          className="px-2 text-neutral-400 hover:text-red-600"
                          data-testid={`tpl-dryrun-remove-${idx}`}
                        >×</button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={startDryRun}
                  disabled={dryRunning || dryConfigs.length === 0 || !dryConfigId}
                  data-testid="tpl-dryrun-start"
                  className="px-4 py-2 text-sm text-white rounded-sm disabled:opacity-50"
                  style={{ background: "#183153" }}
                >
                  {dryRunning ? "Läuft…" : "Testlauf starten"}
                </button>
                {dryRunning && <span className="text-xs text-neutral-500">Wartet auf Provider…</span>}
              </div>

              {dryResult && dryResult.ok && (
                <div className="border border-emerald-200 bg-emerald-50/40 p-3 rounded-sm" data-testid="tpl-dryrun-result-ok">
                  <div className="flex items-center justify-between mb-2">
                    <div className="font-mono text-[10px] uppercase tracking-widest text-emerald-800">Ergebnis · Testlauf, nicht gespeichert</div>
                    <div className="font-mono text-[10px] text-neutral-500">{dryResult.body.provider} · {dryResult.body.model} · {dryResult.body.usage?.input_tokens}/{dryResult.body.usage?.output_tokens} tok · {dryResult.body.latency_ms}ms</div>
                  </div>
                  <pre className="text-xs whitespace-pre-wrap bg-white border border-neutral-200 p-3 max-h-72 overflow-y-auto" data-testid="tpl-dryrun-result-text">{dryResult.body.text}</pre>
                </div>
              )}

              {dryResult && !dryResult.ok && (
                <div className="border border-red-300 bg-red-50 px-3 py-2 text-xs text-red-900" data-testid="tpl-dryrun-result-error">
                  {dryResult.msg}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="px-5 py-3 border-t border-neutral-200 bg-neutral-50 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2 flex-wrap">
            {canEdit && !editing && (
              <button onClick={() => setEditing(true)} data-testid="tpl-edit-button" className="px-3 py-1.5 text-xs border border-neutral-300 rounded-sm hover:bg-white">
                Inhalt bearbeiten
              </button>
            )}
            {dryAllowed && !dryOpen && (
              <button
                onClick={() => setDryOpen(true)}
                data-testid="tpl-dryrun-button"
                className="px-3 py-1.5 text-xs border border-[#183153] text-[#183153] rounded-sm hover:bg-[#183153]/5"
              >
                Trockendurchlauf
              </button>
            )}
            {!dryAllowed && (tpl.status === "active" || tpl.status === "archived") && canManage && (
              <span className="text-[11px] text-neutral-500 italic flex items-center gap-1">
                <Lock className="w-3 h-3" /> Trockendurchlauf nur auf Entwürfen / Review-Templates
              </span>
            )}
            {!canEdit && tpl.status === "active" && !canManage && (
              <div className="text-[11px] text-neutral-500 italic flex items-center gap-1">
                <Lock className="w-3 h-3" /> Aktive Templates sind eingefroren — neue Version anlegen
              </div>
            )}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {allowedTargets.map((s) => {
              const enabled = canDoStatus(s);
              const tooltip = !canManage ? "Nur intern_architekt/admin"
                : (s === "active" && !isAdmin) ? "Nur admin darf aktivieren"
                : "";
              return (
                <button
                  key={s}
                  onClick={() => enabled && setPendingStatus({ target: s, note: "" })}
                  disabled={!enabled}
                  title={tooltip}
                  data-testid={`tpl-status-btn-${s}`}
                  className={`px-3 py-1.5 text-xs rounded-sm ${enabled ? "border border-neutral-300 hover:bg-white" : "border border-neutral-200 text-neutral-400 cursor-not-allowed"}`}
                >
                  → {TPL_STATUS_LABEL[s]}
                </button>
              );
            })}
            <button onClick={onClose} className="px-3 py-1.5 text-xs text-white rounded-sm" style={{ background: "#183153" }}>Schließen</button>
          </div>
        </div>
      </div>
    </div>
  );
}
